import { useEffect, useState } from "react";
import { api, tokens, errMsg } from "../lib/api";
import { Eye, LogIn, UserPlus } from "lucide-react";

const Logo = () => (
  <div className="inline-flex flex-col rounded-2xl bg-white px-3.5 py-2 leading-none text-brand shadow-sm">
    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-deep">Cimol</span>
    <span className="text-lg font-extrabold">Bojot AA</span>
  </div>
);

function LoginForm({ members, onAuthed }) {
  const [memberId, setMemberId] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!memberId) return setErr("Pilih nama Anda dulu");
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { memberId, pin });
      tokens.set(r.data.token);
      onAuthed();
    } catch (ex) {
      setErr(errMsg(ex));
      setPin("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <div>
        <label className="label" htmlFor="login-name">Nama</label>
        <select id="login-name" data-testid="login-name-select" className="field" value={memberId} onChange={(e) => setMemberId(e.target.value)}>
          <option value="">— Pilih nama —</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.role}</option>)}
        </select>
      </div>
      <div>
        <label className="label" htmlFor="login-pin">PIN</label>
        <input id="login-pin" data-testid="login-pin-input" className="field tracking-[0.4em]" type="password" inputMode="numeric"
          maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••" />
      </div>
      {err && <p data-testid="login-error" className="rounded-xl bg-brand-soft px-3 py-2 text-sm font-semibold text-brand">{err}</p>}
      <button data-testid="login-submit-button" disabled={busy} className="btn-primary w-full"><LogIn size={16} /> Masuk</button>
      {!members.length && <p className="text-sm text-[#7a6667]">Belum ada anggota terdaftar. Silakan daftar dulu.</p>}
    </form>
  );
}

function RegisterForm({ jobFunctions, onAuthed }) {
  const [f, setF] = useState({ name: "", role: "", email: "", pin: "", pin2: "", phone: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k.startsWith("pin") ? e.target.value.replace(/\D/g, "") : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.role) return setErr("Pilih fungsi kerja");
    if (f.pin !== f.pin2) return setErr("Konfirmasi PIN tidak sama");
    setBusy(true);
    try {
      const r = await api.post("/auth/register", { name: f.name, role: f.role, email: f.email, pin: f.pin, phone: f.phone });
      tokens.set(r.data.token);
      onAuthed();
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <div>
        <label className="label" htmlFor="reg-name">Nama panggilan</label>
        <input id="reg-name" data-testid="register-name-input" className="field" value={f.name} onChange={set("name")} placeholder="mis. Agoy" />
      </div>
      <div>
        <label className="label" htmlFor="reg-role">Fungsi kerja</label>
        <select id="reg-role" data-testid="register-role-select" className="field" value={f.role} onChange={set("role")}>
          <option value="">— Pilih fungsi —</option>
          {jobFunctions.map((j) => <option key={j.name} value={j.name}>{j.name} ({j.prefix})</option>)}
        </select>
      </div>
      <div>
        <label className="label" htmlFor="reg-email">Email kantor</label>
        <input id="reg-email" data-testid="register-email-input" className="field" type="email" value={f.email} onChange={set("email")} placeholder="nama@citarasakuliner.com" />
      </div>
      <div>
        <label className="label" htmlFor="reg-phone">No WhatsApp <span className="font-normal text-[#7a6667]">(opsional, untuk pengingat)</span></label>
        <input id="reg-phone" data-testid="register-phone-input" className="field" inputMode="tel" value={f.phone} onChange={set("phone")} placeholder="081234567890" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="reg-pin">PIN (4–6 angka)</label>
          <input id="reg-pin" data-testid="register-pin-input" className="field" type="password" inputMode="numeric" maxLength={6} value={f.pin} onChange={set("pin")} />
        </div>
        <div>
          <label className="label" htmlFor="reg-pin2">Ulangi PIN</label>
          <input id="reg-pin2" data-testid="register-pin-confirm-input" className="field" type="password" inputMode="numeric" maxLength={6} value={f.pin2} onChange={set("pin2")} />
        </div>
      </div>
      {err && <p data-testid="register-error" className="rounded-xl bg-brand-soft px-3 py-2 text-sm font-semibold text-brand">{err}</p>}
      <button data-testid="register-submit-button" disabled={busy} className="btn-primary w-full"><UserPlus size={16} /> Daftar & masuk</button>
    </form>
  );
}

export default function AuthScreen({ onAuthed, onViewOnly }) {
  const [tab, setTab] = useState("login");
  const [members, setMembers] = useState([]);
  const [jobFunctions, setJobFunctions] = useState([]);
  useEffect(() => {
    api.get("/auth/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/meta").then((r) => setJobFunctions(r.data.jobFunctions)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-brand px-5 pb-24 pt-8 text-white sm:px-10">
        <div className="mx-auto flex max-w-md items-center gap-3">
          <Logo />
          <div>
            <div className="text-xl font-extrabold leading-tight">Daily Tracker</div>
            <div className="text-xs text-white/80">Marketing & Branding · CRK Group</div>
          </div>
        </div>
        <p className="mx-auto mt-8 max-w-md text-2xl font-extrabold leading-snug sm:text-3xl">Laporan harian tim, cukup 2 menit sehari.</p>
      </div>
      <div className="mx-auto -mt-16 max-w-md px-4 pb-12">
        <div className="card fade-up p-5 shadow-[0_20px_50px_-30px_rgba(31,26,26,.5)] sm:p-6">
          <div className="mb-5 grid grid-cols-2 rounded-full bg-brand-soft p-1">
            {[["login", "Masuk"], ["register", "Daftar"]].map(([k, l]) => (
              <button key={k} data-testid={`auth-tab-${k}`} onClick={() => setTab(k)}
                className={`min-h-[44px] rounded-full text-sm font-bold transition-colors ${tab === k ? "bg-white text-brand shadow-sm" : "text-brand-ink/70"}`}>
                {l}
              </button>
            ))}
          </div>
          {tab === "login" ? <LoginForm members={members} onAuthed={onAuthed} /> : <RegisterForm jobFunctions={jobFunctions} onAuthed={onAuthed} />}
        </div>
        <button data-testid="view-only-button" onClick={onViewOnly} className="btn-ghost mt-4 w-full"><Eye size={16} /> Lihat dashboard saja</button>
        <p className="mt-3 text-center text-xs text-[#7a6667]">Mode lihat saja: tanpa form & tanpa data pribadi.</p>
      </div>
    </div>
  );
}
